import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronRight, MapPin, Search, Sparkles } from 'lucide-react';
import { PROPERTY_TYPES, labelForPropertyType, normalisePropertyType } from '../utils/propertyTypes';
import { useMarket } from '../hooks/useMarket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IRELAND_COUNTIES = [
  'dublin', 'cork', 'galway', 'limerick', 'waterford', 'kerry', 'mayo',
  'donegal', 'wicklow', 'meath', 'kildare', 'wexford', 'tipperary',
  'clare', 'sligo', 'louth', 'kilkenny', 'westmeath',
];

const titleCase = (s) => (s || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Long-tail SEO landing page for `/type/:typeSlug/:county?`.
 *
 * Each URL is a Google-friendly answer to one specific buyer query, e.g.
 *   /type/site-with-planning/galway        → "Sites with planning permission for sale in Galway"
 *   /type/detached/dublin                  → "Detached houses for sale in Dublin"
 *   /type/apartment                        → "Apartments for sale in Ireland"
 *
 * The page injects unique `<title>`, meta description, canonical, OG
 * tags, and JSON-LD ItemList structured data so Google can index the
 * grid as a search result.
 */
const PropertyTypeLandingPage = () => {
  const { typeSlug, county } = useParams();
  const slug = normalisePropertyType(typeSlug);
  const typeLabel = labelForPropertyType(typeSlug);
  const countyLabel = county ? titleCase(county) : 'Ireland';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (slug) params.propertyType = slug;
        if (county) params.location = countyLabel;
        const { data } = await axios.get(`${BACKEND_URL}/api/properties`, { params });
        if (!cancelled) {
          setItems(data || []);
          setTotal((data || []).length);
        }
      } catch (_) {
        if (!cancelled) {
          setItems([]); setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, county, countyLabel]);

  const headlineNoun = slug && slug.startsWith('site-')
    ? typeLabel.toLowerCase()
    : typeLabel.toLowerCase() + 's';
  const headline = `${typeLabel}${slug && slug.startsWith('site-') ? 's' : 's'} for sale in ${countyLabel}`
    .replace('Sites', 'Sites'); // no-op guard

  // Cleaner headline strings.
  const h1 = slug && slug.startsWith('site-')
    ? `${typeLabel} in ${countyLabel}`
    : `${typeLabel}s for sale in ${countyLabel}`;

  const descrPrefix = slug && slug.startsWith('site-')
    ? `Browse the latest ${typeLabel.toLowerCase()} listed for sale`
    : `Browse the latest ${typeLabel.toLowerCase()}s for sale`;
  const metaDescription =
    `${descrPrefix} in ${countyLabel}${county ? ', ' + (market.name || 'Ireland') : ''}. ` +
    `${total} listing${total === 1 ? '' : 's'} from verified ${market.code === 'GB' ? 'UK' : market.code === 'US' ? 'US' : 'Irish'} estate agents. ` +
    `Filter by price, beds and ${market.code === 'GB' ? 'EPC' : market.code === 'US' ? 'Home Energy Score' : 'BER'} on lovinahome.com.`;

  const canonical = county
    ? `https://lovinahome.com/type/${typeSlug}/${county}`
    : `https://lovinahome.com/type/${typeSlug}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: h1,
    url: canonical,
    description: metaDescription,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: items.length,
      itemListElement: items.slice(0, 20).map((p, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'SingleFamilyResidence',
          name: p.title,
          url: `https://lovinahome.com/property/${p.id || p._id}`,
          address: {
            '@type': 'PostalAddress',
            addressLocality: p.location?.area || p.location?.city,
            addressRegion: p.location?.county,
            addressCountry: p.location?.country || 'IE',
          },
          ...(p.price ? {
            offers: { '@type': 'Offer', price: p.price, priceCurrency: 'EUR' },
          } : {}),
        },
      })),
    },
  };

  const pageTitle = `${h1} · lovinahome`;
  const ogTitle = `${h1} · lovinahome`;
  const jsonLdStr = JSON.stringify(jsonLd);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="property-type-landing">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{jsonLdStr}</script>
      </Helmet>

      {/* Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-sm text-gray-500 flex items-center gap-2 overflow-x-auto whitespace-nowrap">
          <Link to="/" className="hover:text-purple-700">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/listings" className="hover:text-purple-700">For Sale</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/type/${typeSlug}`} className="hover:text-purple-700">{typeLabel}</Link>
          {county && (
            <>
              <ChevronRight className="h-3 w-3" />
              <span className="text-gray-900 font-medium">{countyLabel}</span>
            </>
          )}
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-purple-600 hover:bg-purple-700">
              <Sparkles className="h-3 w-3 mr-1" />
              {total} listings
            </Badge>
            {county && (
              <Badge variant="outline">
                <MapPin className="h-3 w-3 mr-1" />
                {countyLabel}
              </Badge>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
            {h1}
          </h1>
          <p className="text-gray-600 mt-3 max-w-2xl">{metaDescription}</p>
          <div className="flex flex-wrap gap-2 mt-5">
            <Link to={`/listings?propertyType=${typeSlug}${county ? `&location=${county}` : ''}`}>
              <Button className="bg-purple-600 hover:bg-purple-700" data-testid="open-full-filters-btn">
                <Search className="h-4 w-4 mr-2" />
                Refine with full filters
              </Button>
            </Link>
            <Link to="/saved-searches">
              <Button variant="outline">Save this search</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Listings grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading listings…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border" data-testid="ptype-landing-empty">
            <p className="text-lg font-semibold text-gray-900">
              No {typeLabel.toLowerCase()}{slug && slug.startsWith('site-') ? '' : 's'} match yet in {countyLabel}.
            </p>
            <p className="text-gray-600 mt-2">
              Be the first to know — save this search and we&apos;ll email you the moment one is listed.
            </p>
            <Link to="/saved-searches">
              <Button className="mt-4 bg-purple-600 hover:bg-purple-700">Save this search</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ptype-landing-grid">
            {items.map((p) => (
              <PropertyCard key={p.id || p._id} property={p} />
            ))}
          </div>
        )}

        {/* Cross-link block — boosts internal link graph for SEO */}
        <div className="mt-12 p-6 bg-white border rounded-lg" data-testid="ptype-crosslinks">
          <h2 className="font-semibold text-gray-900 mb-3">
            {county ? `Browse ${typeLabel.toLowerCase()}s in other counties` : `Browse other property types in ${countyLabel}`}
          </h2>
          <div className="flex flex-wrap gap-2">
            {(county
              ? IRELAND_COUNTIES.filter((c) => c !== county).slice(0, 10).map((c) => ({
                  label: titleCase(c),
                  to: `/type/${typeSlug}/${c}`,
                }))
              : PROPERTY_TYPES.filter((t) => t.slug !== typeSlug).map((t) => ({
                  label: t.label,
                  to: `/type/${t.slug}`,
                }))
            ).map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-purple-100 rounded-full text-gray-700 hover:text-purple-700"
                data-testid={`ptype-crosslink-${c.to.replace(/\//g, '-')}`}
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyTypeLandingPage;
